<template>
    <div id="manual-fax-component">
        <card>
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h1 class="card-title">Send Fax</h1>
                </div>
            </template>
            <b-row align-v="baseline" class="mb-1">
                <b-col>
                    <b-form-group>
                        <h6>Fax Number</h6>
                        <b-input v-model="faxNumber" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row align-v="baseline" class="mb-1">
                <b-col>
                    <b-form-group label="Note on Fax:" :description="`${faxNote.length}/2000 characters`">
                        <b-textarea v-model="faxNote" maxlength="2000" rows="3" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-tabs pills card>
                <b-tab title="Upload Documents">
                    <vue-dropzone id="dropzone" ref="myVueDropzone" :options="dropzoneOptions"
                        @vdropzone-success="dropzoneUploadSuccess" @vdropzone-sending="dropzoneSending" />
                    <b-button variant="outline-primary" :disabled="!validFax" @click="previewFax" block size="lg" class="mt-4">
                        <strong>Preview</strong>
                    </b-button>
                    <b-button variant="primary" :disabled="disableFaxButton" @click="sendFax" block size="lg" class="mt-2">
                        <strong>Send Fax</strong>
                    </b-button>
                </b-tab>
                <b-tab v-if="scanners.length" title="Scan Documents">
                    <label>Scanner:</label>
                    <b-select v-model="selectedScanner" :options="scanners" />

                    <b-overlay :show="isScanning" class="mt-4">
                        <b-button class="m-0" variant="primary" :disabled="isScanning || !validFax" @click="scan" block
                            size="lg">
                            <strong>Scan Documents and Send</strong>
                        </b-button>
                    </b-overlay>
                </b-tab>
            </b-tabs>
        </card>

        <FaxPreviewModal ref="previewModal" modal-id="manual-fax-preview" />
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Ref, Watch } from 'vue-property-decorator';
import Axios, { AxiosRequestConfig } from 'axios';
import Vue2Dropzone from 'vue2-dropzone';
import SearchComponent from "@/components/SearchComponent.vue";
import PatientDocumentsModal from '@/components/Patient/PatientDocumentsModal.vue';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { LockHandler } from '@/mixins/LockHandler';
import IngestionImage from "@/components/IngestionImage.vue";
import Dropzone from "dropzone";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { DatePicker } from 'element-ui';
import TextUtils from '@/util/TextUtils';
import FaxPreviewModal from "@/components/Fax/FaxPreviewModal.vue";
import ClientServiceWorkerMixin from '@/mixins/ClientServiceWorkerMixin';
import { scanResponseToBlob } from '@/util/scanResponse';

@Component({
    name: "ManualFaxComponent",
    components: {
        "VueDropzone": Vue2Dropzone,
        SearchComponent,
        PatientDocumentsModal,
        IngestionImage,
        Checkbox,
        FaxPreviewModal,
        [DatePicker.name]: DatePicker
    },
})
export default class ManualFaxComponent extends Mixins(LockHandler, ClientServiceWorkerMixin) {

    private scanners: Array<string> = [];
    private selectedScanner: string = "";
    private isScanning: boolean = false;
    @Ref("myVueDropzone") private dropzone!: Dropzone;
    @Ref("previewModal") private previewModal!: FaxPreviewModal;
    private faxNumber: string = "";
    private faxNote: string = "";
    private pendingUploads: Array<{ file: any; imageId: number; note: string }> = [];
    private isSending: boolean = false;

    created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");
        this.getAvailableScanners();
    }

    beforeDestroy() {
        console.log(`%c Destroying ${this.$options.name}`, "color: red");
        if (this.hubTimeout) {
            clearTimeout(this.hubTimeout);
        }
    }

    protected onClientServiceWorkerMessage(e: MessageEvent): void {
        const [messageType, data] = e.data;

        switch (messageType) {
            case "clientServiceScanners":
                console.log("Received scanners from client service hub", data);
                if (this.hubTimeout) {
                    clearTimeout(this.hubTimeout);
                    this.hubTimeout = null;
                }
                if (Array.isArray(data)) {
                    this.scanners = data.map((s: any) => s.name || s.id);
                    const previouslySelectedScanner = this.persistentScanner;
                    if (this.scanners.indexOf(previouslySelectedScanner) >= 0) {
                        this.selectedScanner = previouslySelectedScanner;
                    }
                }
                break;
            case "clientServiceScanResponse":
                console.log("Received scan response from client service hub", data);
                this.isScanning = false;
                if (this.hubTimeout) {
                    clearTimeout(this.hubTimeout);
                    this.hubTimeout = null;
                }
                if (!data) {
                    this.$notification(NotificationOptions.error("Scan failed: no response received"));
                    break;
                }
                if (data.error) {
                    this.$notification(NotificationOptions.error(data.error));
                } else {
                    this.handleScanResponse(data);
                }
                break;
        }
    }

    getAvailableScanners() {
        const workerPort = (this as any).$workerPort;
        // Try to use hub first if worker is available
        if (workerPort) {
            console.log("Requesting scanners from client service hub via worker");
            workerPort.postMessage(['clientServiceRefreshScanners', null]);

            // Set timeout to fallback to HTTP if hub doesn't respond
            this.hubTimeout = setTimeout(() => {
                console.log("Hub timeout, falling back to HTTP for scanners");
                this.getAvailableScannersFallback();
            }, 2000);
        } else {
            // Fallback to HTTP immediately if no worker
            this.getAvailableScannersFallback();
        }
    }

    getAvailableScannersFallback() {
        Axios.get('http://localhost:7274/scanner').then((result) => {
            this.scanners = result.data.map((s: any) => s.name);
            const previouslySelectedScanner = this.persistentScanner;
            if (this.scanners.indexOf(previouslySelectedScanner) >= 0) {
                this.selectedScanner = previouslySelectedScanner;
            }
            if (this.hubTimeout) {
                clearTimeout(this.hubTimeout);
                this.hubTimeout = null;
            }
        }).catch((err: any) => {
            console.warn(err);
        });
    }

    get persistentScanner(): any {
        return localStorage.getItem('ingestion_selected_scanner');
    }

    @Watch('selectedScanner')
    selectedScannerChanged(val1: any, _val2: any) {
        localStorage.setItem('ingestion_selected_scanner', val1);
    }

    get validFax(): boolean {
        return this.faxNumber.length >= 10;
    }

    private faxErrorMessage(): string {
        const digits = this.faxNumber.replace(/\D/g, '');
        const isValidUsShape =
            digits.length === 10 ||
            (digits.length === 11 && digits.startsWith('1'));
        return isValidUsShape
            ? "Failed to send fax. Please contact support."
            : "Failed to send fax. Please ensure the fax number is in the correct format: +15551234567 or 15551234567";
    }

    // dropzone
    // properties
    get dropzoneOptions(): any {
        return {
            url: `${Axios.defaults.baseURL}/image?bypassIngestion=true`,
            thumbnailWidth: 150,
            withCredentials: true,
            paramName: 'images',
            autoProcessQueue: false,
        };
    }

    // events
    dropzoneSending(_file: any, xhr: XMLHttpRequest, _formData: FormData) {
        xhr.setRequestHeader("Authorization", Axios.defaults.headers["Authorization"]?.toString() ?? "");
    }

    get disableFaxButton(): boolean {
        return !this.validFax || this.isSending;
    }

    async sendFax() {
        await this.sendPendingFaxes();
        this.dropzone.processQueue();
    }

    // Renders the preview endpoint's PDF (document + note cover page) for the first queued document
    // without faxing. The file is sent directly to the preview endpoint, so no Images row is persisted
    // (the document is only uploaded when the user actually sends).
    previewFax() {
        const queued = (this.dropzone.getQueuedFiles?.() ?? []) as any[];
        const file = queued[0] ?? (this.dropzone.getAcceptedFiles?.() ?? [])[0];
        if (!file) {
            this.$notification(NotificationOptions.error("Add a document to preview."));
            return;
        }

        this.previewModal.open(async () => {
            const formData = new FormData();
            formData.append("file", file, file.name);
            formData.append("faxNumber", TextUtils.formatFaxNumber(this.faxNumber));
            formData.append("note", this.faxNote ?? "");
            const previewResp = await Axios.post("api/Fax/preview-upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                responseType: "blob",
            });
            return previewResp.data;
        });
    }

    async dropzoneUploadSuccess(file: any, response: number[]) {
        // Capture the note that's set right now — sendPendingFaxes runs serialized via
        // isSending, so a later upload completing after an earlier batch already cleared
        // this.faxNote would otherwise be sent with no note.
        this.pendingUploads.push({ file, imageId: response[0], note: this.faxNote });
        await this.sendPendingFaxes();
    }

    private async sendPendingFaxes() {
        if (this.pendingUploads.length === 0) return;
        if (this.isSending) return;

        this.isSending = true;
        try {
            const remaining: Array<{ file: any; imageId: number; note: string }> = [];
            let successCount = 0;
            let failureCount = 0;
            let lastSentNote = "";
            for (const entry of this.pendingUploads) {
                try {
                    await Axios.post("api/Fax/Send", {
                        imageId: entry.imageId,
                        faxNumber: TextUtils.formatFaxNumber(this.faxNumber),
                        note: entry.note,
                    });
                    this.dropzone.removeFile(entry.file);
                    successCount++;
                    lastSentNote = entry.note;
                } catch (err) {
                    remaining.push(entry);
                    failureCount++;
                }
            }
            this.pendingUploads = remaining;

            if (successCount > 0) {
                // Only clear the textarea if the user hasn't already typed a new note
                // for the next batch since these entries were captured.
                if (this.faxNote === lastSentNote) {
                    this.faxNote = "";
                }
                this.$notification(NotificationOptions.success("Fax sent successfully."));
            }
            if (failureCount > 0) {
                this.$notification(NotificationOptions.error(this.faxErrorMessage()));
            }
        } finally {
            this.isSending = false;
        }
    }

    scan() {
        if (this.selectedScanner == "") {
            this.$notification(NotificationOptions.error("Please select a scanner."));
            return;
        }
        this.isScanning = true;

        const workerPort = (this as any).$workerPort;
        // Try to use hub first if worker is available
        if (workerPort) {
            console.log("Requesting scan from client service hub via worker");
            workerPort.postMessage(['clientServiceScan', this.selectedScanner]);

            // Set timeout to fallback to HTTP if hub doesn't respond
            this.hubTimeout = setTimeout(() => {
                console.log("Hub timeout, falling back to HTTP for scan");
                this.scanFallback();
            }, 5000); // Longer timeout for scanning since it takes time
        } else {
            // Fallback to HTTP immediately if no worker
            this.scanFallback();
        }
    }

    scanFallback() {
        const scanConfig: AxiosRequestConfig = {
            params: { scannerId: this.selectedScanner },
            responseType: 'blob',
        };
        Axios.get('http://localhost:7274/scanner/scan', scanConfig)
            .then(result => {
                this.handleScanResponse(result.data);
            })
            .catch(error => {
                console.log(error);
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.isScanning = false;
                if (this.hubTimeout) {
                    clearTimeout(this.hubTimeout);
                    this.hubTimeout = null;
                }
            });
    }

    handleScanResponse(data: any) {
        const blob = scanResponseToBlob(data);
        if (!blob) {
            console.error("Unexpected scan response format", data);
            this.$notification(NotificationOptions.error("Unexpected scan response format"));
            return;
        }

        const formData = new FormData();
        formData.append("images", blob, "scan.pdf");
        const imageConfig = {
            headers: { "Content-Type": "multipart/form-data" },
            params: { bypassIngestion: true },
        };
        Axios.post('/image', formData, imageConfig)
            .then(response => {
                Axios.post("api/Fax/Send", {
                    imageId: response.data[0],
                    faxNumber: TextUtils.formatFaxNumber(this.faxNumber),
                    note: this.faxNote,
                })
                    .then(_ => {
                        this.faxNote = "";
                        this.$notification(NotificationOptions.success("Fax sent successfully."));
                    })
                    .catch(() => {
                        this.$notification(NotificationOptions.error(this.faxErrorMessage()));
                    });
            })
            .catch(error => {
                console.log(error);
                this.$notification(NotificationOptions.error(error));
            });
    }

}
</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
::v-deep table#escript-queue .flip-list-move {
    transition: transform .5s;
}

/*noinspection CssUnusedSymbol*/

::v-deep .selected-col {
    width: 2rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .button-col {
    width: 10rem;
    padding-top: 0;
    padding-bottom: 0;
}

/*noinspection CssUnusedSymbol*/
::v-deep .date-col {
    width: 10rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .type-col {
    width: 11rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .ingestion-item {
    min-height: 20rem;
    max-height: 40rem;
}

::v-deep(.locked),
::v-deep(.completed) {
    width: 200px;
}

::v-deep(.vue-dropzone) {
    border: 2px dashed red;
}

::v-deep(.dz-clickable) {
    border: 2px dashed green;
}
</style>
